import type {
  Alignment,
  HorizontalAlignment,
  LateralAlignment,
  VerticalAlignment,
} from "building-types/hosting/hostingTypes";
import { ANGLE_EPSILON, DIST_EPSILON } from "geom/core";
import { line2Eval, line2MidPt, line2Normal, line2Vec } from "geom/line2";
import type { Vec2 } from "geom/vec2";
import { vec2AngleBetweenCW, vec2Scale, vec2Sub } from "geom/vec2";

/**
 * operates only in the XY plane
 */
function lineEvalAlignment(mirror: boolean, line: number[], { horizontal, lateral }: Alignment = {}): Vec2 {
  const [x0, y0, x1, y1] = line;
  let [px, py] = line2MidPt(x0, y0, x1, y1);
  if (horizontal) {
    const { param = 0, paramNormalized, alignment } = horizontal;
    if (param !== undefined && paramNormalized !== undefined) {
      let lx: number;
      let ly: number;
      let xa = x0;
      let ya = y0;
      let xb = x1;
      let yb = y1;
      if (mirror) {
        [xa, ya, xb, yb] = [xb, yb, xa, ya];
      }
      switch (alignment) {
        case "LEFT":
          [lx, ly] = line2Eval(xa, ya, xb, yb, param, paramNormalized);
          break;
        case "RIGHT":
          [lx, ly] = line2Eval(xb, yb, xa, ya, param, paramNormalized);
          break;
        default:
        case "CENTER":
          [lx, ly] = line2Eval(xa, ya, xb, yb, 0.5, true);
          break;
      }

      if (!isNaN(lx)) {
        px = lx;
      }
      if (!isNaN(ly)) {
        py = ly;
      }
    }
  }
  if (lateral && lateral.offset) {
    const [nx, ny] = line2Normal(x0, y0, x1, y1);
    const [dx, dy] = vec2Scale(nx, ny, lateral.offset);
    [px, py] = [px - dx, py - dy];
  }
  return [px, py];
}

export interface HostToChildDeltas {
  childMidPt: Vec2;
  translation: Vec2;
  theta: number;
}

export function isAligned({ translation: [tx, ty], theta }: HostToChildDeltas): boolean {
  return Math.abs(tx) < DIST_EPSILON && Math.abs(ty) < DIST_EPSILON && Math.abs(theta % (2 * Math.PI)) < ANGLE_EPSILON;
}

export function hostToChildDeltas(
  mirror: boolean,
  host: number[],
  child: number[],
  alignment: Alignment = {}, // default to empty object to ensure alignment is defined
): HostToChildDeltas {
  let [cx0, cy0, cx1, cy1] = child;

  if (mirror) {
    // reverse the points in the child line if
    // mirrored
    [cx0, cy0, cx1, cy1] = [cx1, cy1, cx0, cy0];
  }

  const [ax, ay] = line2Vec(host);
  const [bx, by] = line2Vec(child);

  const theta = vec2AngleBetweenCW(ax, ay, bx, by) + Math.PI;

  let cx: number;
  let cy: number;
  switch (alignment.horizontal?.alignment) {
    case "LEFT":
      [cx, cy] = [cx1, cy1];
      break;
    case "RIGHT":
      [cx, cy] = [cx0, cy0];
      break;
    default:
    case "CENTER":
      [cx, cy] = line2MidPt(cx0, cy0, cx1, cy1);
      break;
  }

  const [hx, hy] = lineEvalAlignment(mirror, host, alignment);

  const [tx, ty] = vec2Sub(hx, hy, cx, cy);

  return { childMidPt: [cx, cy], theta, translation: [tx, ty] };
}

export function newLateralAlignment(offset: number = 0): LateralAlignment {
  return { offset };
}

export function newHorizontalAlignment(param: number = 0, paramNormalized: boolean = false): HorizontalAlignment {
  return { param, paramNormalized };
}

export function newVerticalAlignment(offset: number = 0): VerticalAlignment {
  return { offset };
}
